import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import RadioButtonGroup from "../../elements/radio-button-group";
import { generateReactKey } from "../../utils/reactKey";
import Icon from "../../elements/icon";
import Heading from "../../elements/heading";
import api from "../../utils/api";
import { connect } from "react-redux";
import { selectUserState } from "../../selectors/application";
import { billingInfoSelector } from "../../selectors/gears-checkout";
import "./style.scss";
import AccountRemoveAddressModal from "../account-remove-address-modal";
import BillingInfoForm from "../billing-info-form";
import { isLoggedIn } from "../../services/siteService";
import { SuperButton } from "../../elements";
import { formatSavedAddress } from "../../utils/siteUtils";
import AddressInformation from "../address-information";

const BillingInfo = React.forwardRef(({ fields, user, billingFormData, addressList, noAddress, loadAddressList, addAddress, updateAddress, removeAddress, visible }, ref) => {
  const { headingType, heading, addressUnsavedMessage, addressMissingMessage, removeAddressModal, ctaNameChange, ctaTypeChange, colorSchemeChange, iconNameChange } = fields;
  const componentRoot = useRef();
  const addAddressRef = useRef();
  const editAddressRef = useRef(null);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const [showRemoveAddressModal, setShowRemoveAddressModal] = useState(null);
  const [editAddress, setEditAddress] = useState(null);
  const [addressEditing, setAddressEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  useImperativeHandle(ref, () => ({
    validateForm() {
      if (isLoggedIn() && addressList.length > 0) {
        if (editAddress) {
          editAddressRef.current.setError(addressUnsavedMessage.value);
          editAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        } else if (selectedAddress) {
          if (selectedAddress.address) {
            return {
              isValid: true,
              formData: selectedAddress.address
            };
          } else {
            addAddressRef.current.setError(addressUnsavedMessage.value);
            addAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
            return {
              isValid: false,
              formData: null
            };
          }
        } else {
          setFormError(addressMissingMessage.value);
          componentRoot.current.scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        }
      } else {
        return addAddressRef.current.validateForm();
      }
    },
    startEdit() {
      setAddressEditing(true);
    }
  }));
  useEffect(() => {
    setDefaultAddress({
      firstName: billingFormData?.firstName || user?.firstName || "",
      lastName: billingFormData?.lastName || user?.lastName || "",
      street: billingFormData?.street || user?.street || "",
      apartment: billingFormData?.apartment || user?.apartment || "",
      postalCode: billingFormData?.postalCode || user?.postalCode || "",
      city: billingFormData?.city || user?.city || "",
      countryId: billingFormData?.countryId + "",
      countryCode: billingFormData?.countryCode
    });
  }, [user, billingFormData, addAddressRef]);
  useEffect(() => {
    let _selectedAddress = null;
    if (selectedAddress) {
      if (selectedAddress.address) {
        _selectedAddress = addressList.find(x => x.value === selectedAddress.address?.addressId);
      } else {
        _selectedAddress = addressList.find(x => x.isNew);
        if (!_selectedAddress) {
          _selectedAddress = addressList.find(x => x.address?.defaultFlag == "1");
        }
      }
    }
    if (!_selectedAddress) {
      if (billingFormData?.addressId) {
        _selectedAddress = addressList.find(x => x.value === billingFormData.addressId);
      } else {
        _selectedAddress = addressList.find(x => x.address?.defaultFlag == "1");
        if (!_selectedAddress) {
          _selectedAddress = addressList[0];
        }
      }
    }
    setSelectedAddress(_selectedAddress);
  }, [addressList, billingFormData]);
  const onAddressChange = address => {
    if (selectedAddress !== address) {
      setSelectedAddress(address);
      if (editAddress && editAddress !== address) {
        setEditAddress(null);
      }
      if (address.value !== "new") {
        setAddressEditing(false);
      } else {
        setDefaultAddress({
          firstName: user?.firstName || "",
          lastName: user?.lastName || ""
        });
      }
    }
  };
  const removeAddressModalProps = {
    ...removeAddressModal.fields,
    showModal: showRemoveAddressModal,
    onModalClose: evt => {
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    },
    onSubmit: async evt => {
      await api.address.delete(null, {
        addressId: addressToRemove.value
      });
      removeAddress(addressToRemove);
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    }
  };
  const onEditClick = (evt, option) => {
    evt.stopPropagation();
    setEditAddress(option);
  };
  const onRemoveClick = (evt, option) => {
    evt.stopPropagation();
    setAddressToRemove(option);
    setShowRemoveAddressModal(true);
  };
  const addAddressFormProps = {
    fields,
    ref: addAddressRef,
    address: defaultAddress,
    savable: addressList.length > 0,
    async onSaveSuccess(address) {
      setEditAddress(null);
      await addAddress(address);
      setAddressEditing(false);
    }
  };
  const ctaChangeAddressProps = ctaNameChange.value && {
    buttonText: ctaNameChange.value,
    ctaType: ctaTypeChange,
    colorScheme: colorSchemeChange,
    iconName: iconNameChange,
    callback(evt) {
      setAddressEditing(true);
    }
  };
  const shippingLayout = option => {
    if (option.address) {
      if (editAddress === option) {
        const editAddressFormProps = {
          fields,
          ref: editAddressRef,
          address: option.address,
          onSaveSuccess() {
            setSelectedAddress(editAddress);
            updateAddress(editAddress);
            setEditAddress(null);
            setAddressEditing(false);
          }
        };
        return (
          <div className="edit-address-block" key={generateReactKey()} onClick={e => e.stopPropagation()}>
            <p className="label">Edit Address</p>
            <BillingInfoForm {...editAddressFormProps} />
          </div>
        );
      } else {
        return (
          <div className="saved-address-block" key={generateReactKey()}>
            <div className="body-container">
              {formatSavedAddress(option.address).map(row => {
                return (
                  <p className="content-row" key={generateReactKey()}>
                    {row}
                  </p>
                );
              })}
            </div>
            <div className="icons">
              <Icon name="Edit" colorScheme="outline-green" className="icon --visible" onClick={evt => onEditClick(evt, option)} />
              {selectedAddress !== option && addressList.length > 2 && option.address.defaultFlag != "1" && <Icon name="Delete" colorScheme="outline-green" className="icon --visible" onClick={evt => onRemoveClick(evt, option)} />}
            </div>
          </div>
        );
      }
    } else {
      return <p className="label">{option.label}</p>;
    }
  };
  if (visible) {
    return (
      <div className="billing-info" ref={componentRoot}>
        <div className="title">
          <Heading type={headingType} html={heading} />
        </div>
        {!addressEditing && selectedAddress && <AddressInformation className="default-address" address={selectedAddress.address} />}
        {isLoggedIn() && addressEditing && <RadioButtonGroup options={addressList} html={shippingLayout} alignment="top" onChange={onAddressChange} selectedOption={selectedAddress} />}
        {(!isLoggedIn() || (selectedAddress && selectedAddress.value === "new") || addressList.length === 0) && <BillingInfoForm {...addAddressFormProps} />}
        {!addressEditing && addressList.length > 0 && (
          <div className="cta-edit-address">
            <SuperButton {...ctaChangeAddressProps} />
          </div>
        )}
        {formError && <div className="failed-message">{formError}</div>}
        <AccountRemoveAddressModal {...removeAddressModalProps} />
      </div>
    );
  } else {
    return null;
  }
});
BillingInfo.displayName = "BillingInfo";
const mapStateToProps = state => ({
  user: selectUserState(state),
  billingFormData: billingInfoSelector(state)
});
export default connect(mapStateToProps, null, null, { forwardRef: true })(BillingInfo);
